import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import { hallTicketsAPI, examAPI, studentAPI } from '../../services/api';

const today = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  exam_id: '',
  student_id: '',
  exam_center: 'Main Campus',
  room_no: '',
  seat_no: '',
  issued_date: today(),
};

const emptyBatchForm = {
  exam_id: '',
  exam_center: 'Main Campus',
  issued_date: today(),
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '-');

export default function HallTickets() {
  const [tickets, setTickets] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [batchData, setBatchData] = useState(emptyBatchForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [ticketRes, examRes, studentRes] = await Promise.all([
        hallTicketsAPI.getAll(),
        examAPI.getAll(),
        studentAPI.getAll({ status: 'ACTIVE' }),
      ]);
      setTickets(Array.isArray(ticketRes.data) ? ticketRes.data : []);
      setExams(Array.isArray(examRes.data) ? examRes.data : []);
      setStudents(Array.isArray(studentRes.data) ? studentRes.data : []);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error loading hall ticket data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const stats = useMemo(() => ({
    total: tickets.length,
    issued: tickets.filter((ticket) => ticket.issued_date).length,
    pendingSeat: tickets.filter((ticket) => !ticket.room_no || !ticket.seat_no).length,
  }), [tickets]);

  const selectedExam = exams.find((exam) => String(exam.id) === String(formData.exam_id));
  const filteredStudents = selectedExam?.standard
    ? students.filter((student) => String(student.standard) === String(selectedExam.standard))
    : students;

  const handleOpenDialog = (ticket = null) => {
    if (ticket) {
      setEditingTicket(ticket);
      setFormData({
        exam_id: ticket.exam_id || '',
        student_id: ticket.student_id || '',
        exam_center: ticket.exam_center || 'Main Campus',
        room_no: ticket.room_no || '',
        seat_no: ticket.seat_no || '',
        issued_date: ticket.issued_date || today(),
      });
    } else {
      setEditingTicket(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'exam_id' ? { student_id: '' } : {}),
    }));
  };

  const handleBatchInputChange = (event) => {
    const { name, value } = event.target;
    setBatchData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.exam_id || !formData.student_id) {
      showSnackbar('Exam and student are required', 'error');
      return;
    }

    try {
      if (editingTicket) {
        await hallTicketsAPI.update(editingTicket.id, {
          exam_center: formData.exam_center,
          room_no: formData.room_no,
          seat_no: formData.seat_no,
        });
        showSnackbar('Hall ticket updated successfully');
      } else {
        await hallTicketsAPI.create(formData);
        showSnackbar('Hall ticket created successfully');
      }
      setOpenDialog(false);
      setEditingTicket(null);
      fetchAllData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving hall ticket', 'error');
    }
  };

  const handleBatchGenerate = async () => {
    if (!batchData.exam_id) {
      showSnackbar('Exam is required', 'error');
      return;
    }

    try {
      const response = await hallTicketsAPI.generateBatch(batchData);
      showSnackbar(response.data?.message || 'Hall tickets generated successfully');
      setOpenBatchDialog(false);
      fetchAllData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error generating hall tickets', 'error');
    }
  };

  const handleDelete = async (ticket) => {
    if (!window.confirm(`Delete hall ticket ${ticket.ticket_no}?`)) return;

    try {
      await hallTicketsAPI.delete(ticket.id);
      showSnackbar('Hall ticket deleted successfully');
      fetchAllData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting hall ticket', 'error');
    }
  };

  const handlePrint = (ticket) => {
    const printWindow = window.open('', '_blank', 'height=700,width=760');
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Hall Ticket - ${ticket.ticket_no}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 28px; color: #111827; }
            .ticket { border: 2px solid #111827; padding: 28px; max-width: 680px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #111827; padding-bottom: 16px; margin-bottom: 22px; }
            h1 { font-size: 22px; margin: 0; }
            h2 { font-size: 15px; margin: 6px 0 0; text-transform: uppercase; letter-spacing: 1px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
            .field { border: 1px solid #d1d5db; padding: 10px 12px; border-radius: 6px; }
            .label { display: block; font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
            .value { font-size: 15px; font-weight: 700; }
            .footer { display: flex; justify-content: space-between; margin-top: 46px; align-items: end; }
            .sig { width: 160px; border-top: 1px solid #111827; text-align: center; padding-top: 6px; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="ticket">
            <div class="header">
              <h1>Basaveswhara lower primery school</h1>
              <p style="margin: 0; font-size: 14px; color: #666;">Belur, Chikkanasabi</p>
              <h2>Exam Hall Ticket</h2>
            </div>
            <div class="grid">
              <div class="field"><span class="label">Ticket No.</span><span class="value">${ticket.ticket_no || '-'}</span></div>
              <div class="field"><span class="label">Issued Date</span><span class="value">${formatDate(ticket.issued_date)}</span></div>
              <div class="field"><span class="label">Exam</span><span class="value">${ticket.exam_name || '-'}</span></div>
              <div class="field"><span class="label">Exam Type</span><span class="value">${ticket.exam_type || '-'}</span></div>
              <div class="field"><span class="label">Student</span><span class="value">${ticket.student_name || '-'}</span></div>
              <div class="field"><span class="label">Enrollment No.</span><span class="value">${ticket.enrollment_no || '-'}</span></div>
              <div class="field"><span class="label">Center</span><span class="value">${ticket.exam_center || '-'}</span></div>
              <div class="field"><span class="label">Room / Seat</span><span class="value">${ticket.room_no || '-'} / ${ticket.seat_no || '-'}</span></div>
            </div>
            <div class="footer">
              <span>Bring this ticket and school ID card to the exam center.</span>
              <div class="sig">Authorized Signature</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const filteredTickets = tickets.filter((ticket) => {
    const text = [
      ticket.ticket_no,
      ticket.exam_name,
      ticket.student_name,
      ticket.enrollment_no,
      ticket.exam_center,
      ticket.room_no,
      ticket.seat_no,
    ].join(' ').toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
        Hall Tickets
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate, assign rooms, and print exam hall tickets.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Tickets', value: stats.total },
          { label: 'Issued', value: stats.issued },
          { label: 'Need Room/Seat', value: stats.pendingSeat },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Toolbar sx={{ px: 0, mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Ticket
        </Button>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={() => {
            setBatchData(emptyBatchForm);
            setOpenBatchDialog(true);
          }}
        >
          Batch Generate
        </Button>
        <TextField
          placeholder="Search ticket, exam, student, room..."
          size="small"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', md: 360 }, ml: { md: 'auto' } }}
        />
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Exam</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Center</TableCell>
                <TableCell align="center">Room</TableCell>
                <TableCell align="center">Seat</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{ticket.ticket_no}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{ticket.exam_name || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{ticket.exam_type || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{ticket.student_name || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{ticket.enrollment_no || '-'}</Typography>
                    </TableCell>
                    <TableCell>{ticket.exam_center || '-'}</TableCell>
                    <TableCell align="center">{ticket.room_no || '-'}</TableCell>
                    <TableCell align="center">{ticket.seat_no || '-'}</TableCell>
                    <TableCell>{formatDate(ticket.issued_date)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(ticket)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handlePrint(ticket)} title="Print">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(ticket)} title="Delete" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    No hall tickets found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTicket ? 'Edit Hall Ticket' : 'Add Hall Ticket'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Exam"
              name="exam_id"
              value={formData.exam_id}
              onChange={handleInputChange}
              disabled={Boolean(editingTicket)}
            >
              {exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.exam_name} - Class {exam.standard}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Student"
              name="student_id"
              value={formData.student_id}
              onChange={handleInputChange}
              disabled={Boolean(editingTicket)}
            >
              {filteredStudents.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.student_name} ({student.enrollment_no}) - Class {student.standard}
                  {student.section ? `-${student.section}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Exam Center"
              name="exam_center"
              value={formData.exam_center}
              onChange={handleInputChange}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Room No"
                  name="room_no"
                  value={formData.room_no}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Seat No"
                  name="seat_no"
                  value={formData.seat_no}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Issued Date"
                  name="issued_date"
                  type="date"
                  value={formData.issued_date}
                  onChange={handleInputChange}
                  disabled={Boolean(editingTicket)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBatchDialog} onClose={() => setOpenBatchDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Batch Generate Hall Tickets</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Batch generation creates tickets for all active students in the selected exam's class.
            </Alert>
            <TextField
              select
              fullWidth
              label="Exam"
              name="exam_id"
              value={batchData.exam_id}
              onChange={handleBatchInputChange}
            >
              {exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.exam_name} - Class {exam.standard}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Exam Center"
              name="exam_center"
              value={batchData.exam_center}
              onChange={handleBatchInputChange}
            />
            <TextField
              fullWidth
              label="Issued Date"
              name="issued_date"
              type="date"
              value={batchData.issued_date}
              onChange={handleBatchInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBatchDialog(false)}>Cancel</Button>
          <Button onClick={handleBatchGenerate} variant="contained">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
